/**
 * ArSelectionScopeBar
 *
 * The one place the user chooses between the two selection scopes: the rows on
 * screen, and every row the table holds. It appears only once every visible row
 * is ticked AND the table holds more than that, so on an unfiltered single-page
 * table — where the two scopes name the same set — there is nothing to offer and
 * no bar in the layout (SYD-2386).
 *
 * Rendered as a full-width row of `artable-container`'s grid, directly under the
 * header, so it scrolls with the rows it describes.
 *
 * Escalating is a decision about THIS selection, never a table setting: it
 * survives no page change, no hand tick and no clear, all of which route back
 * through `clearSelection` or reset `scope` at their own call site. It does not
 * survive the visible set widening either — `resolveSelectionScope` retires it
 * the moment some visible row is unticked, so the bar drops back to its
 * "select all" state rather than vanishing with `'all'` still stored.
 *
 * Above `AR_TABLE_MAX_ROWS` the escalation is disabled rather than absent, and
 * says why. The `span` around that button is load-bearing: a disabled antd
 * Button emits no mouse events, so a Tooltip on it alone never opens.
 *
 * Both scope buttons are disabled while `isDispatchingWorkflowRun` is set: the
 * per-recipient documents a workflow run is building read the live selection, so
 * an escalation landing mid-run widens every document built after it (SYD-2410).
 * `ArTableBody` renders this bar only off-portal, so PDF export mode never sees
 * the lock.
 */

import { FC, memo, useContext } from 'react';
import { Button, Tooltip, Typography, theme } from 'antd';
import { useSnapshot } from 'valtio';
import { ArTableUIStateContext } from '../context';
import { formStore } from '@/store';
import { resolveSelectionScope } from './resolveSelectionScope';
import { AR_TABLE_MAX_ROWS } from '#types/System.Parameters.types';

/** Why both scope buttons are dead while a workflow run holds the row scope. */
const SCOPE_LOCKED_BY_RUN = 'A workflow run is building its documents from these rows. Wait for it to finish.';

const ArSelectionScopeBar: FC = () => {
  const { token } = theme.useToken();
  const uiState = useContext(ArTableUIStateContext)!;
  const selectableSnap = useSnapshot(uiState.selectableRowIds);
  const selectionSnap = useSnapshot(uiState.selection);
  const paginationSnap = useSnapshot(uiState.pagination);
  const { isDispatchingWorkflowRun } = useSnapshot(formStore.state);

  /**
   * The same derivation `ArHeaderCheckbox` ticks against, so "every visible row
   * is ticked" means the same thing in both places.
   */
  const { visibleRowIds: visibleIds, everyVisibleTicked, isAllRows } = resolveSelectionScope({
    selectableRowIds: selectableSnap,
    selectedRows: selectionSnap.selectedRows,
    scope: selectionSnap.scope,
  });
  const totalRows = paginationSnap.totalRows;

  if (!everyVisibleTicked || totalRows <= visibleIds.length) return null;

  const aboveCap = totalRows > AR_TABLE_MAX_ROWS;

  return (
    <div
      aria-label="artable-selection-scope-bar"
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: token.marginXS,
        padding: `${token.paddingXXS}px ${token.paddingSM}px`,
        backgroundColor: isAllRows ? token.colorPrimaryBg : token.colorFillQuaternary,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {isAllRows ? (
        <>
          <Typography.Text>
            {`All ${totalRows.toLocaleString()} rows in this table selected, including rows hidden by filters and rows on other pages.`}
          </Typography.Text>
          <Button
            type="link"
            size="small"
            aria-label="select-visible-rows-only"
            disabled={isDispatchingWorkflowRun}
            onClick={() => { uiState.selection.scope = 'visible'; }}
          >
            Select visible rows only
          </Button>
        </>
      ) : (
        <>
          <Typography.Text>
            {`All ${visibleIds.length.toLocaleString()} visible rows selected.`}
          </Typography.Text>
          <Tooltip
            title={
              isDispatchingWorkflowRun
                ? SCOPE_LOCKED_BY_RUN
                : aboveCap
                  ? `This table holds more than ${AR_TABLE_MAX_ROWS.toLocaleString()} rows. Narrow it with a filter, then select all.`
                  : undefined
            }
          >
            <span style={{ display: 'inline-flex' }}>
              <Button
                type="link"
                size="small"
                aria-label="select-all-table-rows"
                disabled={aboveCap || isDispatchingWorkflowRun}
                onClick={() => { uiState.selection.scope = 'all'; }}
              >
                {`Select all ${totalRows.toLocaleString()} rows in this table`}
              </Button>
            </span>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default memo(ArSelectionScopeBar);
